/**
 * VariantPickerSets - Custom element for product set variant selection
 * Handles product bundle creation, pricing, and cart operations
 */
class VariantPickerSets extends HTMLElement {

  static SELECTORS = {
    ITEM: '.variant-picker-sets__item',
    OPTIONS: '.variant-picker-sets__options',
    CHOICE: '.variant-picker-sets__choice',
    CHOICE_LABEL: '.variant-picker-sets__choice-label',
    CHECKBOX: '.sets-choice__checkbox',
    VALUE_BUTTON: '.variant-picker-sets__value',
    CTA_BUTTON: '.sets-sticky-cta__button',
    PRICE_BLOCK: '[data-static-price]',
    VARIANTS_SCRIPT: 'script[data-variants]',
    MEDIA_BUTTON: '[data-action="open-gallery"]',
    MEDIA_SCRIPT: 'script[data-media]',
    GALLERY_MODAL: '#sets-product-gallery-modal',
  };


  static ATTRIBUTES = {
    ACTION_TOGGLE: '[data-action="toggle-item"]',
    ACTION_ATC: '[data-action="sets-atc"]',
    PRODUCT_ID: 'productId',
    OPTION_POSITION: 'optionPosition',
    VALUE: 'value',
    SPECIAL_PRICE: 'specialPriceCents',
  };


  static CLASSES = {
    OPEN: 'is-open',
    SELECTED: 'is-selected',
    INCLUDED: 'is-included',
    LINE_THROUGH: 'line-through',
  };


  static DEFAULTS = {
    CURRENCY: 'USD',
    MONEY_FORMAT: '${{amount}}',
    MIN_SELECTIONS: 1,
    CENTS_DIVISOR: 100,
  };


  static LABELS = {
    ADD_TO_SET: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).addToSetPlain,
    INCLUDED: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).included,
    SELECT_PRODUCTS: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).selectProducts,
    ADD_TO_CART: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).addToCart,
    ADD_SET: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).addSet,
  };

  // Set Group Configuration
  static SET_GROUP = {
    TAG_PREFIX: 'set_group:',
    SEPARATOR: ':',
  };

  // Required Product Tags
  // Also can be set in theme settings
  static REQUIRED_TAGS = {
    BRA: ['bra', 'Bra', 'swim_top', 'bikini_top'],
    PANTY: ['panty', 'panties', 'Panty', 'Panties', 'swim_bottom', 'bikini_bottom'],
  };

  constructor() {
    super();
    // Bind once so removeEventListener can find the same reference across reconnects
    this._clickHandler = this.handleClick.bind(this);
    this.initializeProperties();
    this.cacheElements();
  }

  initializeProperties() {
    this.requiredCount = this.parseNumber(this.dataset.requiredCount);
    this.parentVariantId = this.dataset.parentVariantId;
    this.parentPriceCents = this.parseNumber(this.dataset.parentPriceCents);
    this.setGroup = this.dataset.setGroup || '';
    this.context = this.dataset.context || 'main_product';
    this.galleryModalId = this.dataset.galleryModalId || VariantPickerSets.SELECTORS.GALLERY_MODAL.replace('#', '');
    this.selectedItems = new Map();
    this.moneyFormat = window.themeVariables?.settings?.moneyFormat || VariantPickerSets.DEFAULTS.MONEY_FORMAT;
    this.requiredTags = this._resolveRequiredTags();
  }

  _resolveRequiredTags() {
    const parseTags = (str) => str ? str.split(',').map(t => t.trim()).filter(Boolean) : null;
    const braTags = parseTags(window.themeVariables?.settings?.setsBraTags);
    const pantyTags = parseTags(window.themeVariables?.settings?.setsPantyTags);
    return {
      BRA: braTags || VariantPickerSets.REQUIRED_TAGS.BRA,
      PANTY: pantyTags || VariantPickerSets.REQUIRED_TAGS.PANTY,
    };
  }

  cacheElements() {
    this.ctaButtons = this.querySelectorAll(VariantPickerSets.SELECTORS.CTA_BUTTON);
    this.items = null;
    this.stickyCta = this.querySelector('.sets-sticky-cta');
    this.staticCta = this.querySelector('.sets-static-cta');
  }

  connectedCallback() {
    this.bindEvents();
    this.initializeSetGroups();
    this.initializeStickyObserver();
    this.updateCta();
  }

  initializeStickyObserver() {
    if (!this.staticCta || !this.stickyCta) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          this.stickyCta.classList.add('is-visible');
        } else {
          this.stickyCta.classList.remove('is-visible');
        }
      });
    }, {
      root: null,
      threshold: 0,
      rootMargin: "0px"
    });

    observer.observe(this.staticCta);
  }

  bindEvents() {
    // Remove before adding to prevent duplicate listeners when connectedCallback fires
    // multiple times (e.g. when Modal moves element to document.body via shouldAppendToBody)
    this.removeEventListener('click', this._clickHandler);
    this.addEventListener('click', this._clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._clickHandler);
  }


  parseNumber(value, fallback = 0) {
    const parsed = Number(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  safeQuerySelector(element, selector) {
    try {
      return element?.querySelector(selector) || null;
    } catch {
      return null;
    }
  }

  getCurrency() {
    return Shopify?.currency?.active || window.Shopify?.currency || VariantPickerSets.DEFAULTS.CURRENCY;
  }

  formatMoney(cents) {
    try {
      const value = this.parseNumber(cents) / VariantPickerSets.DEFAULTS.CENTS_DIVISOR;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.getCurrency()
      }).format(value);
    } catch {
      return `${cents / VariantPickerSets.DEFAULTS.CENTS_DIVISOR}`;
    }
  }

  // Set Group Management
  initializeSetGroups() {
    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(itemEl => {
      const itemSetGroup = this.getProductSetGroup(itemEl);
      itemEl.dataset.itemSetGroup = itemSetGroup || '';
    });
  }

  extractSetGroupFromTags(tags) {
    if (!Array.isArray(tags)) return null;

    const setGroupTag = tags.find(tag =>
      tag && tag.startsWith(VariantPickerSets.SET_GROUP.TAG_PREFIX)
    );

    if (!setGroupTag) return null;

    const parts = setGroupTag.split(VariantPickerSets.SET_GROUP.SEPARATOR);
    return parts.length > 1 ? parts[1].trim() : null;
  }

  extractAllSetGroupsFromTags(tags) {
    if (!Array.isArray(tags)) return [];

    return tags
      .filter(tag => tag && tag.startsWith(VariantPickerSets.SET_GROUP.TAG_PREFIX))
      .map(tag => {
        const parts = tag.split(VariantPickerSets.SET_GROUP.SEPARATOR);
        return parts.length > 1 ? parts[1].trim() : null;
      })
      .filter(Boolean);
  }

  getProductSetGroup(itemEl) {
    const groups = this.getProductSetGroups(itemEl);
    return groups.length > 0 ? groups[0] : null;
  }

  getProductSetGroups(itemEl) {
    if (!itemEl) return [];

    try {
      if (itemEl.dataset && itemEl.dataset.productTags) {
        const tags = itemEl.dataset.productTags.split(',').map(tag => tag.trim());
        return this.extractAllSetGroupsFromTags(tags);
      }

      const productData = this.getItemVariants(itemEl)?.[0];
      if (productData?.product?.tags) {
        return this.extractAllSetGroupsFromTags(productData.product.tags);
      }

      return [];
    } catch (error) {
      console.error('Set Group Debug - Error getting product set groups:', error);
      return [];
    }
  }

  getProductTags(itemEl) {
    if (!itemEl) return [];

    try {
      if (itemEl.dataset && itemEl.dataset.productTags) {
        return itemEl.dataset.productTags.split(',').map(tag => tag.trim());
      }

      const productData = this.getItemVariants(itemEl)?.[0];
      if (productData?.product?.tags) {
        return Array.isArray(productData.product.tags) ? productData.product.tags : [];
      }

      return [];
    } catch {
      return [];
    }
  }

  hasRequiredTag(itemEl, requiredTags) {
    const productTags = this.getProductTags(itemEl);
    return requiredTags.some(requiredTag =>
      productTags.some(tag => tag.toLowerCase() === requiredTag.toLowerCase())
    );
  }

  countProductsWithTag(requiredTags) {
    let count = 0;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) continue;

      if (this.hasRequiredTag(itemEl, requiredTags)) {
        count++;
      }
    }

    return count;
  }

  countAllProductsWithTag(requiredTags) {
    let count = 0;

    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    for (const itemEl of allItems) {
      if (this.hasRequiredTag(itemEl, requiredTags)) {
        count++;
      }
    }

    return count;
  }

  validateSetGroupConsistency() {
    if (this.selectedItems.size <= 1) return { isValid: true, setGroup: null };

    // Collect set groups per product, then find intersection across all products.
    // A product may belong to multiple sets (multiple set_group: tags), so we need
    // to find a set group that ALL selected products share.
    let intersection = null;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);

      if (!itemEl) {
        console.warn('Set Group Debug - Could not find item element for productId:', productId);
        continue;
      }

      const productSetGroups = this.getProductSetGroups(itemEl);

      if (productSetGroups.length === 0) {
        // Product has no set_group tags — it can't belong to any set group
        intersection = [];
        break;
      }

      if (intersection === null) {
        intersection = productSetGroups;
      } else {
        intersection = intersection.filter(g => productSetGroups.includes(g));
      }

      if (intersection.length === 0) break;
    }

    const commonGroups = intersection || [];
    const isValid = commonGroups.length > 0;
    const setGroup = isValid ? commonGroups[0] : null;

    return { isValid, setGroup, groupCount: commonGroups.length };
  }

  isValidCompleteSet() {
    const { isValid, setGroup } = this.validateSetGroupConsistency();

    if (!isValid || !setGroup) return { isComplete: false, setGroup: null };

    let setGroupCount = 0;
    let hasBraProduct = false;
    let hasPantyProduct = false;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) continue;

      if (this.getProductSetGroups(itemEl).includes(setGroup)) {
        setGroupCount++;

        if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) hasBraProduct = true;
        if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) hasPantyProduct = true;
      }
    }

    const { braAvailable, pantyAvailable } = this._getSetTypesAvailable(setGroup);
    const bothTypesRequired = braAvailable && pantyAvailable;
    const typeRequirementMet = !bothTypesRequired || (hasBraProduct && hasPantyProduct);

    // Set is complete only if we have required count AND type requirements are met.
    // For KISSBUNDLES sets, use the bundle component count as the effective minimum.
    const effectiveRequired = this._getEffectiveMin();
    const isComplete = effectiveRequired > 0 &&
                      setGroupCount >= effectiveRequired &&
                      typeRequirementMet;

    return { isComplete, setGroup, setGroupCount, hasBraProduct, hasPantyProduct, bothTypesRequired, typeRequirementMet };
  }

  _getSetTypesAvailable(setGroup) {
    let braAvailable = false;
    let pantyAvailable = false;

    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    for (const itemEl of allItems) {
      if (setGroup && !this.getProductSetGroups(itemEl).includes(setGroup)) continue;
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) braAvailable = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) pantyAvailable = true;
      if (braAvailable && pantyAvailable) break;
    }

    return { braAvailable, pantyAvailable };
  }

  _validateTypeCombination() {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    let braAvailable = false;
    let pantyAvailable = false;

    for (const itemEl of allItems) {
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) braAvailable = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) pantyAvailable = true;
      if (braAvailable && pantyAvailable) break;
    }

    if (!braAvailable || !pantyAvailable) {
      return { required: false, hasBra: false, hasPanty: false, isValid: true };
    }

    let hasBra = false;
    let hasPanty = false;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;
      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) continue;
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) hasBra = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) hasPanty = true;
    }

    return { required: true, hasBra, hasPanty, isValid: hasBra && hasPanty };
  }

  handleClick(event) {
    if (this.handleGalleryClick(event)) return;
    if (this.handleToggleClick(event)) return;
    if (this.handleVariantClick(event)) return;
    if (this.handleAddToCartClick(event)) return;
  }

  handleToggleClick(event) {
    const toggleElement = event.target.closest(VariantPickerSets.ATTRIBUTES.ACTION_TOGGLE);
    if (!toggleElement) return false;
    if (!this.contains(toggleElement)) return false;

    if (event.target.matches('input[type="checkbox"]')) {
      event.preventDefault();
    }

    const itemEl = event.target.closest(VariantPickerSets.SELECTORS.ITEM);
    if (!itemEl) return true;

    const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
    const isSelected = this.isItemSelected(productId);
    const isOpen = this.isOptionsOpen(itemEl);

    if (isSelected) {
      this.deselectItem(itemEl, productId);
    } else if (isOpen) {
      this.deselectItem(itemEl, productId);
    } else {
      this.openOptions(itemEl);
    }

    return true;
  }

  handleVariantClick(event) {
    const valueButton = event.target.closest(VariantPickerSets.SELECTORS.VALUE_BUTTON);
    if (!valueButton) return false;
    if (!this.contains(valueButton)) return false;

    // Ignore clicks on disabled buttons
    if (valueButton.disabled || valueButton.dataset.available === 'false') {
      return true;
    }

    const itemEl = valueButton.closest(VariantPickerSets.SELECTORS.ITEM);
    if (!itemEl) return true;

    this.selectVariantOption(itemEl, valueButton);
    return true;
  }

  async handleAddToCartClick(event) {
    const btn = event.target.closest(VariantPickerSets.ATTRIBUTES.ACTION_ATC);
    if (!btn) return false;
    if (!this.contains(btn)) return false;

    // Nothing selected — nothing to add
    if (this.selectedItems.size === 0) {
      this.highlightIncompleteItems();
      return true;
    }

    // Show loading state immediately for instant feedback
    this.setCtaLoading(true);
    await this.addSetToCart();
    this.closest('quick-buy-modal')?.hide();
    return true;
  }

  handleGalleryClick(event) {
    const mediaButton = event.target.closest(VariantPickerSets.SELECTORS.MEDIA_BUTTON);
    if (!mediaButton) return false;
    if (!this.contains(mediaButton)) return false;

    const itemEl = mediaButton.closest(VariantPickerSets.SELECTORS.ITEM);
    if (!itemEl) return true;

    this.openGalleryModal(itemEl);
    return true;
  }

  openGalleryModal(itemEl) {
    const mediaScript = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.MEDIA_SCRIPT);
    if (!mediaScript) return;

    try {
      const media = JSON.parse(mediaScript.textContent);
      const productTitle = itemEl.dataset.productTitle || '';
      const modal = document.getElementById(this.galleryModalId);

      if (!modal || !media || media.length === 0) return;

      // Filter only images
      const imageMedia = media.filter(item => item.media_type === 'image');
      if (imageMedia.length === 0) return;

      // Update modal title
      const titleEl = modal.querySelector('.sets-gallery-modal__title');
      if (titleEl) {
        titleEl.textContent = productTitle;
      }

      // Update full details link
      const productUrl = itemEl.dataset.productUrl;
      const linkEl = modal.querySelector('.sets-gallery-modal__link');
      if (linkEl && productUrl) {
        linkEl.href = productUrl;
      }

      // Render slides
      const slidesContainer = modal.querySelector('.sets-gallery-modal__slides');
      const thumbnailsContainer = modal.querySelector('.sets-gallery-modal__thumbnails');

      if (slidesContainer) {
        slidesContainer.innerHTML = '';
        imageMedia.forEach((item, index) => {
          const slide = document.createElement('div');
          slide.className = 'sets-gallery-modal__slide';
          slide.dataset.index = index;

          const img = document.createElement('img');
          img.src = item.preview_image.url;
          img.alt = item.alt || productTitle;
          img.loading = 'lazy';
          img.className = 'sets-gallery-modal__image';

          slide.appendChild(img);
          slidesContainer.appendChild(slide);
        });
      }

      if (thumbnailsContainer) {
        thumbnailsContainer.innerHTML = '';
        imageMedia.forEach((item, index) => {
          const thumbnail = document.createElement('button');
          thumbnail.type = 'button';
          thumbnail.className = 'sets-gallery-modal__thumbnail';
          thumbnail.dataset.index = index;
          thumbnail.setAttribute('aria-label', (((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).goToImage || '').replace('__INDEX__', index + 1));

          const img = document.createElement('img');
          img.src = item.preview_image.url;
          img.alt = item.alt || productTitle;
          img.loading = 'lazy';

          thumbnail.appendChild(img);
          thumbnail.addEventListener('click', () => this.goToSlide(index));
          thumbnailsContainer.appendChild(thumbnail);
        });
      }

      // Initialize slider
      this.currentSlideIndex = 0;
      this.totalSlides = imageMedia.length;

      // Update slider after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.updateSlider();
      }, 0);

      // Add navigation handlers
      const prevButton = modal.querySelector('.sets-gallery-modal__prev');
      const nextButton = modal.querySelector('.sets-gallery-modal__next');

      if (prevButton) {
        prevButton.onclick = () => this.previousSlide();
      }
      if (nextButton) {
        nextButton.onclick = () => this.nextSlide();
      }

      // Add keyboard navigation
      this.setupKeyboardNavigation(modal);

      // Open modal
      modal.show();
    } catch (error) {
      console.error('Error opening gallery modal:', error);
    }
  }

  setupKeyboardNavigation(modal) {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previousSlide();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.nextSlide();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        modal.hide();
      }
    };

    // Remove existing listener if any
    if (this.galleryKeyDownHandler) {
      modal.removeEventListener('keydown', this.galleryKeyDownHandler);
    }

    // Add new listener
    this.galleryKeyDownHandler = handleKeyDown;
    modal.addEventListener('keydown', this.galleryKeyDownHandler);

    // Clean up on modal close
    const handleClose = () => {
      modal.removeEventListener('keydown', this.galleryKeyDownHandler);
      modal.removeEventListener('dialog:after-hide', handleClose);
      this.galleryKeyDownHandler = null;
    };
    modal.addEventListener('dialog:after-hide', handleClose);
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;
    this.currentSlideIndex = index;
    this.updateSlider();
  }

  nextSlide() {
    if (this.currentSlideIndex < this.totalSlides - 1) {
      this.currentSlideIndex++;
    } else {
      this.currentSlideIndex = 0;
    }
    this.updateSlider();
  }

  previousSlide() {
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--;
    } else {
      this.currentSlideIndex = this.totalSlides - 1;
    }
    this.updateSlider();
  }

  updateSlider() {
    const modal = document.getElementById(this.galleryModalId);
    if (!modal) return;

    const slides = modal.querySelectorAll('.sets-gallery-modal__slide');
    const thumbnails = modal.querySelectorAll('.sets-gallery-modal__thumbnail');
    const prevButton = modal.querySelector('.sets-gallery-modal__prev');
    const nextButton = modal.querySelector('.sets-gallery-modal__next');

    // Update slides visibility
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.currentSlideIndex);
    });

    // Update thumbnails
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('is-active', index === this.currentSlideIndex);
    });

    // Update navigation buttons
    if (prevButton) {
      prevButton.disabled = this.totalSlides <= 1;
    }
    if (nextButton) {
      nextButton.disabled = this.totalSlides <= 1;
    }
  }


  isItemSelected(productId) {
    const selection = this.selectedItems.get(productId);
    return Boolean(selection?.variant);
  }

  isOptionsOpen(itemEl) {
    const options = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.OPTIONS);
    return options?.classList.contains(VariantPickerSets.CLASSES.OPEN) || false;
  }

  deselectItem(itemEl, productId) {
    this.clearSelectionForItem(itemEl);
    this.setChoiceState(itemEl, false);
    this.selectedItems.delete(productId);
    this.closeOptions(itemEl);
    this.updateCta();
  }

  openOptions(itemEl) {
    const options = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.OPTIONS);
    const choice = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHOICE);
    this.animateDropdown(options, true);
    if (choice) choice.classList.add(VariantPickerSets.CLASSES.OPEN);

    // Initialize availability state when opening
    const variants = this.getItemVariants(itemEl);
    if (variants) {
      this.updateVariantAvailability(itemEl, variants);
    }
  }

  closeOptions(itemEl) {
    const options = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.OPTIONS);
    const choice = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHOICE);
    this.animateDropdown(options, false);
    if (choice) choice.classList.remove(VariantPickerSets.CLASSES.OPEN);
  }

  selectVariantOption(itemEl, valueButton) {
    const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
    const variants = this.getItemVariants(itemEl);

    if (!variants) return;

    this.updateOptionSelection(itemEl, valueButton);

    // Always update availability after selecting any option
    this.updateVariantAvailability(itemEl, variants);

    const variant = this.findMatchingVariant(itemEl, productId, variants);

    if (variant) {
      this.applyVariantSelection(itemEl, productId, variant);
    }

    this.updateCta();
  }

  updateOptionSelection(itemEl, selectedButton) {
    const optionFieldset = selectedButton.closest('fieldset');
    if (!optionFieldset) return;

    // Clear previous selections in this option group
    optionFieldset
      .querySelectorAll(VariantPickerSets.SELECTORS.VALUE_BUTTON)
      .forEach(btn => btn.classList.remove(VariantPickerSets.CLASSES.SELECTED));

    selectedButton.classList.add(VariantPickerSets.CLASSES.SELECTED);
  }

  findMatchingVariant(itemEl, productId, variants) {
    const selection = this.selectedItems.get(productId) || { options: {}, variant: null };

    // Get all selected options for this product
    const optionFieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');
    const totalOptions = optionFieldsets.length;
    let selectedCount = 0;

    optionFieldsets.forEach(fieldset => {
      const optionPosition = this.parseNumber(fieldset.dataset[VariantPickerSets.ATTRIBUTES.OPTION_POSITION]);
      const selectedOption = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);

      if (selectedOption) {
        const optionValue = selectedOption.dataset[VariantPickerSets.ATTRIBUTES.VALUE];
        selection.options[optionPosition] = optionValue;
        selectedCount++;
      }
    });

    // Only proceed if all options are selected
    if (selectedCount < totalOptions) {
      return null;
    }

    // Find matching variant that is available
    const matchingVariant = variants.find(variant =>
      Object.keys(selection.options).every(pos =>
        variant[`option${pos}`] === selection.options[pos]
      )
    );

    // Return only if variant is available
    return matchingVariant?.available ? matchingVariant : null;
  }

  applyVariantSelection(itemEl, productId, variant) {
    const selection = this.selectedItems.get(productId) || { options: {}, variant: null };
    const wasSelected = Boolean(selection?.variant);
    variant.product_id = productId; // Store DOM product ID in variant
    selection.variant = variant;
    selection.productId = productId; // Also store in selection
    this.selectedItems.set(productId, selection);

    this.setChoiceState(itemEl, true);
    this.updateItemPrice(itemEl, variant);

    // Show toast message when set is added to bundle (only if it wasn't already selected)
    if (!wasSelected) {
      const itemSetGroup = this.getProductSetGroup(itemEl);
      if (itemSetGroup) {
        this.showToastMessage(this.dataset.toastMessage || 'This set has been added to the bundle');
      }
    }
  }

  getItemVariants(itemEl) {
    try {
      const scriptsElement = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.VARIANTS_SCRIPT);
      return scriptsElement ? JSON.parse(scriptsElement.textContent) : null;
    } catch {
      return null;
    }
  }

  /**
   * Update availability of option buttons based on current selections
   */
  updateVariantAvailability(itemEl, variants) {
    const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];

    // Get currently selected options from DOM
    const currentSelections = {};
    const optionFieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');

    optionFieldsets.forEach(fieldset => {
      const optionPosition = this.parseNumber(fieldset.dataset[VariantPickerSets.ATTRIBUTES.OPTION_POSITION]);
      const selectedButton = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);

      if (selectedButton) {
        const optionValue = selectedButton.dataset[VariantPickerSets.ATTRIBUTES.VALUE];
        currentSelections[optionPosition] = optionValue;
      }
    });

    // Update availability for each option
    optionFieldsets.forEach(fieldset => {
      const optionPosition = this.parseNumber(fieldset.dataset[VariantPickerSets.ATTRIBUTES.OPTION_POSITION]);
      const buttons = fieldset.querySelectorAll(VariantPickerSets.SELECTORS.VALUE_BUTTON);

      buttons.forEach(button => {
        const value = button.dataset[VariantPickerSets.ATTRIBUTES.VALUE];

        // Check if there's any available variant with this value
        const isAvailable = variants.some(variant => {
          if (!variant.available) return false;
          if (variant[`option${optionPosition}`] !== value) return false;

          // Check if this variant matches all currently selected options (except this position)
          return Object.keys(currentSelections).every(pos => {
            const posNum = this.parseNumber(pos);
            if (posNum === optionPosition) return true; // Skip current position being evaluated
            return variant[`option${pos}`] === currentSelections[pos];
          });
        });

        button.disabled = !isAvailable;
        button.dataset.available = isAvailable;
      });
    });
  }

  // UI State Management
  clearSelectionForItem(itemEl) {
    itemEl
      .querySelectorAll(`.${VariantPickerSets.CLASSES.SELECTED}`)
      .forEach(btn => btn.classList.remove(VariantPickerSets.CLASSES.SELECTED));
  }

  setChoiceState(itemEl, included) {
    const checkbox = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHECKBOX);
    const label = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHOICE_LABEL);
    const choiceRow = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHOICE);

    if (checkbox) checkbox.checked = included;
    if (label) label.textContent = included ? VariantPickerSets.LABELS.INCLUDED : VariantPickerSets.LABELS.ADD_TO_SET;
    if (choiceRow) choiceRow.classList.toggle(VariantPickerSets.CLASSES.INCLUDED, included);
  }

  updateItemPrice(itemEl, variant) {
    const priceBlock = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.PRICE_BLOCK);
    if (!priceBlock) return;

    const priceSpan = priceBlock.querySelector('span');
    const compareSpan = priceBlock.querySelector(`.${VariantPickerSets.CLASSES.LINE_THROUGH}`);

    if (priceSpan) priceSpan.textContent = this.formatMoney(variant.price);
    if (compareSpan && variant.compare_at_price) {
      compareSpan.textContent = this.formatMoney(variant.compare_at_price);
    }
  }

  animateDropdown(element, open) {
    if (!element) return;

    element.style.height = `${element.scrollHeight}px`;
    void element.offsetHeight; // Force reflow

    element.classList.toggle(VariantPickerSets.CLASSES.OPEN, open);
    element.style.height = open ? `${element.scrollHeight}px` : '0px';

    const cleanup = () => {
      if (open) element.style.height = 'auto';
      element.removeEventListener('transitionend', cleanup);
    };

    element.addEventListener('transitionend', cleanup);
  }

  // Data Access Methods
  getSelectedVariants() {
    return Array.from(this.selectedItems.values())
      .filter(selection => selection.variant)
      .map(selection => selection.variant);
  }

  getOrderedSelectedVariants() {
    if (!this.items) {
      this.items = Array.from(this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM));
    }

    return this.items.reduce((acc, itemEl) => {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      const selection = this.selectedItems.get(productId);

      if (selection?.variant) {
        acc.push(selection.variant);
      }
      return acc;
    }, []);
  }

  // Price Calculation Methods
  calculateItemPrice(variant, itemEl) {
    if (!itemEl) {
      return this.parseNumber(variant.price);
    }

    const specialPrice = this.parseNumber(itemEl.dataset[VariantPickerSets.ATTRIBUTES.SPECIAL_PRICE]);
    const variantPrice = this.parseNumber(variant.price);

    return specialPrice > 0 ? specialPrice : variantPrice;
  }

  getTotalPriceCents() {
    let total = 0;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      total += this.calculateItemPrice(selection.variant, itemEl);
    }

    return total;
  }

  getBundlePriceCents() {
    const ordered = this.getOrderedSelectedVariants();
    const count = Math.min(this.requiredCount, ordered.length);

    let total = 0;
    for (let i = 0; i < count; i++) {
      const variant = ordered[i];
      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`);
      total += this.calculateItemPrice(variant, itemEl);
    }

    return total;
  }

  calculateTotalPrice() {
    const { isValid, setGroup } = this.validateSetGroupConsistency();

    if (!isValid) {
      return this.getTotalPriceCents();
    }

    const { isComplete, hasBraProduct } = this.isValidCompleteSet();
    let totalCents = 0;

    if (isComplete && hasBraProduct && this.parentPriceCents > 0) {
      totalCents = this.parentPriceCents;

      const setProducts = [];
      const extraProducts = [];

      for (const [productId, selection] of this.selectedItems) {
        if (!selection.variant) continue;

        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
        if (!itemEl) {
          extraProducts.push(selection.variant);
          continue;
        }

        if (this.getProductSetGroups(itemEl).includes(setGroup)) {
          setProducts.push(selection.variant);
        } else {
          extraProducts.push(selection.variant);
        }
      }

      // Use selectBundleProducts to match cart logic (bra + panty priority)
      const bundleProducts = this.selectBundleProducts(setProducts, this.requiredCount);
      const bundleSet = new Set(bundleProducts);
      const remainderSetProducts = setProducts.filter(v => !bundleSet.has(v));

      [...extraProducts, ...remainderSetProducts].forEach(variant => {
        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`);
        totalCents += this.calculateItemPrice(variant, itemEl);
      });
    } else {
      totalCents = this.getTotalPriceCents();
    }

    return Math.max(0, totalCents);
  }

  // CTA Management
  updateCta() {
    if (!this.ctaButtons || this.ctaButtons.length === 0) {
      this.ctaButtons = this.querySelectorAll(VariantPickerSets.SELECTORS.CTA_BUTTON);
    }

    if (!this.ctaButtons || this.ctaButtons.length === 0) return;

    const hasSelections = this.selectedItems.size >= 1;
    const { isValid, setGroup } = this.validateSetGroupConsistency();
    const { isComplete, setGroupCount, hasBraProduct } = this.isValidCompleteSet();
    const totalCents = this.calculateTotalPrice();

    this.updateSetGroupStatus(setGroup, isValid, isComplete, setGroupCount);

    let buttonText = VariantPickerSets.LABELS.SELECT_PRODUCTS;

    if (hasSelections && totalCents > 0) {
      if (isComplete && isValid && hasBraProduct) {
        buttonText = `${VariantPickerSets.LABELS.ADD_SET} • ${this.formatMoney(totalCents)}`;
      } else {
        buttonText = `${VariantPickerSets.LABELS.ADD_TO_CART} • ${this.formatMoney(totalCents)}`;
      }
    }

    this.ctaButtons.forEach(btn => {
      btn.textContent = buttonText;
    });
  }

  updateSetGroupStatus(setGroup, isValid, isComplete, setGroupCount) {
    this.classList.toggle('has-set-group', Boolean(setGroup));
    this.classList.toggle('has-valid-set-group', isValid);
    this.classList.toggle('has-complete-set', isComplete);

    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(itemEl => {
      if (itemEl) {
        const itemSetGroups = this.getProductSetGroups(itemEl);
        const itemSetGroup = itemSetGroups[0] || '';
        itemEl.toggleAttribute('data-in-set-group', Boolean(setGroup) && itemSetGroups.includes(setGroup));
        itemEl.dataset.itemSetGroup = itemSetGroup;
      }
    });

    // let statusEl = this.querySelector('.set-group-status');
    // if (!statusEl) {
    //   statusEl = document.createElement('div');
    //   statusEl.className = 'set-group-status';
    //   const firstChild = this.querySelector('.v-stack');
    //   if (firstChild) {
    //     firstChild.appendChild(statusEl);
    //   }
    // }

    // // Only show status if minimal set is collected
    // if (setGroup && isValid) {
    //   // Count products with the current setGroup
    //   let currentSetGroupCount = 0;
    //   for (const [productId, selection] of this.selectedItems) {
    //     if (!selection.variant) continue;
    //     const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
    //     if (itemEl && this.getProductSetGroup(itemEl) === setGroup) {
    //       currentSetGroupCount++;
    //     }
    //   }

    //   const braCount = this.countProductsWithTag(VariantPickerSets.REQUIRED_TAGS.BRA);
    //   const totalPantyCount = this.countAllProductsWithTag(VariantPickerSets.REQUIRED_TAGS.PANTY);
    //   const hasMinimalSet = currentSetGroupCount >= this.requiredCount && braCount > 0;

    //   if (hasMinimalSet && totalPantyCount >= 2) {
    //     statusEl.textContent = 'Claim extra panties at $25, no code needed.';
    //     statusEl.className = 'set-group-status set-group-status--complete';
    //   } else {
    //     statusEl.textContent = '';
    //     statusEl.className = 'set-group-status set-group-status--hidden';
    //   }
    // } else {
    //   statusEl.textContent = '';
    //   statusEl.className = 'set-group-status set-group-status--hidden';
    // }
  }

  // Loading state for CTA buttons
  setCtaLoading(isLoading) {
    this.ctaButtons.forEach(btn => {
      if (isLoading) {
        btn.setAttribute('aria-busy', 'true');
        btn.dataset.originalText = btn.textContent;
        btn.style.position = 'relative';
        btn.style.pointerEvents = 'none';
      } else {
        btn.removeAttribute('aria-busy');
        if (btn.dataset.originalText) {
          btn.textContent = btn.dataset.originalText;
          delete btn.dataset.originalText;
        }
        btn.style.position = '';
        btn.style.pointerEvents = '';
      }
    });
  }

  // Cart Operations
  async addSetToCart() {
    const selectedVariants = this.getSelectedVariants();
    if (selectedVariants.length === 0) {
      this.setCtaLoading(false);
      return;
    }

    try {
      const items = this.prepareCartItems(selectedVariants);
      await this.submitToCart(items);
      await this.handleCartSuccess();
    } catch (error) {
      console.error('Error adding set to cart:', error);
      this.handleCartError(error);
    } finally {
      this.setCtaLoading(false);
    }
  }

  /**
   * Returns the effective minimum selection count for the current set.
   * For KISSBUNDLES sets: smallest valid bundle component count.
   * For legacy sets: requiredCount metafield or MIN_SELECTIONS.
   * Falls back to legacy if all bundles have no components (broken app data).
   */
  _getEffectiveMin() {
    if (window.KISSBUNDLES?.bundles?.length) {
      const min = Math.min(...window.KISSBUNDLES.bundles.map(b => {
        const count = b.components ? Object.keys(b.components).filter(k => k).length : 0;
        return count > 0 ? count : Infinity;
      }));
      if (isFinite(min)) return min;
      // All bundles have broken/empty components → fall back to legacy
    }
    return this.requiredCount > 0 ? this.requiredCount : VariantPickerSets.DEFAULTS.MIN_SELECTIONS;
  }

  selectBundleProducts(setProducts, count) {
    if (setProducts.length <= count) {
      return setProducts.slice();
    }

    const bras = [];
    const panties = [];

    setProducts.forEach(variant => {
      const itemEl = this.querySelector(
        `${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`
      );
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) {
        bras.push(variant);
      } else if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) {
        panties.push(variant);
      }
    });

    // If both types present — anchor bundle with one bra + one panty first
    if (bras.length > 0 && panties.length > 0) {
      const selected = [bras[0], panties[0]];

      if (selected.length >= count) {
        return selected.slice(0, count);
      }

      // Fill remaining slots in original selection order
      const selectedSet = new Set(selected);
      for (const variant of setProducts) {
        if (!selectedSet.has(variant) && selected.length < count) {
          selected.push(variant);
          selectedSet.add(variant);
        }
      }

      return selected;
    }

    // Fallback: original selection order
    return setProducts.slice(0, count);
  }

  prepareCartItems(selectedVariants) {
    // === NEW BUNDLE LOGIC ===
    if (window.KISSBUNDLES?.bundles?.length) {
      const picker      = new KissBundlePicker(window.KISSBUNDLES, window.KISS_VMAPS);
      const splitOption = window.KISSBUNDLES.split_option || null;

      // Phase 1: collect handles + raw component option values (before any mapping)
      const collectedItems = [];
      const splitValues = []; // per-component split values (must all match)

      for (const [productId, selection] of this.selectedItems) {
        if (!selection.variant) continue;
        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
        if (!itemEl) continue;

        const handle = itemEl.dataset.productHandle;
        const rawOptions = {}; // component option name → value (unmapped)

        itemEl.querySelectorAll('fieldset[data-option-position]').forEach(fs => {
          const pos   = fs.dataset.optionPosition;
          const name  = fs.querySelector('legend')?.textContent?.trim();
          const value = selection.variant[`option${pos}`];
          if (!name || !value) return;
          if (splitOption && name === splitOption) {
            splitValues.push(value);
          }
          rawOptions[name] = value;
        });

        collectedItems.push({ handle, variant: selection.variant, rawOptions });
      }

      // If split_option is set, all components must have the same value.
      // Mixed colors (bra=yellow, panty=black) → no bundle → fallback to legacy.
      let splitValue = null;
      if (splitOption) {
        const uniqueSplitValues = [...new Set(splitValues)];
        if (uniqueSplitValues.length !== 1) {
          console.warn('[KissBundles] Mixed split values — falling back to legacy:', uniqueSplitValues);
          // fall through to legacy
        } else {
          splitValue = uniqueSplitValues[0];
        }
      }

      // Phase 2: find bundle by handles (option_map not needed here)
      const allHandles = collectedItems.map(i => i.handle);
      const bundle = splitOption && splitValues.length > 0 && new Set(splitValues).size !== 1
        ? null
        : picker.findBundle(allHandles, splitValue);

      if (bundle) {
        // Phase 3: separate bundle items from extras
        const bundleComponentHandles = Object.values(bundle.components);
        const bundleItems = collectedItems.filter(i => bundleComponentHandles.includes(i.handle));
        const extraItems  = collectedItems.filter(i => !bundleComponentHandles.includes(i.handle));

        // Phase 4: build selectedOptions using per-component option maps.
        // bundle.component_option_maps: { [handle]: { compOptionName: bundleOptionName } }
        // Falls back to flat bundle.option_map, then global option_map, then name as-is.
        // Per-component maps are required when two components share the same option name
        // (e.g. bra "Size" → "Elara Size", panty "Size" → "Olivia Size").
        const componentOptionMaps = bundle.component_option_maps || {};
        const globalFlatMap = bundle.option_map || window.KISSBUNDLES.option_map || {};
        const selectedOptions = {};
        bundleItems.forEach(item => {
          const componentMap = componentOptionMaps[item.handle] || globalFlatMap;
          for (const [compName, value] of Object.entries(item.rawOptions)) {
            // If the component option name is already a bundle-level name, keep it as-is
            const bundleName = (bundle.option_order || []).includes(compName)
              ? compName
              : (componentMap[compName] || compName);
            selectedOptions[bundleName] = value;
          }
        });

        const variant = picker.findVariant(bundle, selectedOptions);

        if (variant && variant.available) {
          const items = [{ id: variant.id, quantity: 1, properties: { _set_url: window.location.pathname } }];
          extraItems.forEach(i => items.push({ id: i.variant.id, quantity: 1 }));
          return items;
        }

        if (variant && !variant.available) {
          console.warn('[KissBundles] Out of stock:', variant.id);
          return [];
        }

        console.error('[KissBundles] Variant not found — falling back to legacy:', { selectedOptions, option_order: bundle.option_order });
      } else {
        console.warn('[KissBundles] Bundle not found — falling back to legacy:', { allHandles, splitValue });
      }
    }
    // === END BUNDLE LOGIC — falls through to legacy if bundle/variant not found ===

    const { isValid, setGroup } = this.validateSetGroupConsistency();
    const { isComplete, hasBraProduct } = this.isValidCompleteSet();

    if (!isComplete || !isValid || !hasBraProduct || !this.parentVariantId) {
      return selectedVariants.map(variant => ({ id: variant.id, quantity: 1 }));
    }

    const items = [];
    const bundleId = `${setGroup || this.setGroup || 'set'}-${Date.now()}`;

    const setProducts = [];
    const extraProducts = [];

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) {
        extraProducts.push(selection.variant);
        continue;
      }

      if (this.getProductSetGroups(itemEl).includes(setGroup)) {
        setProducts.push(selection.variant);
      } else {
        extraProducts.push(selection.variant);
      }
    }

    const bundleProducts = this.selectBundleProducts(setProducts, this.requiredCount);
    const bundleSet = new Set(bundleProducts);
    const remainderSetProducts = setProducts.filter(v => !bundleSet.has(v));

    [...extraProducts, ...remainderSetProducts].forEach(variant => {
      items.push({ id: variant.id, quantity: 1 });
    });

    const labels = bundleProducts.map(variant => variant.name || variant.title);
    const parentProps = {
      _set_group: setGroup || this.setGroup,
      _set_role: 'parent',
      _set_bundle_id: bundleId,
      _set_type: setGroup ? `set_group:${setGroup}` : 'custom_set'
    };

    labels.forEach((label, index) => {
      parentProps[`Product ${index + 1}`] = label;
    });

    items.push({ id: this.parentVariantId, quantity: 1, properties: parentProps });

    return items;
  }

  async submitToCart(items) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Submit to Cart Debug - Error response:', errorText);
      throw new Error(`Cart submission failed: ${response.status} - ${errorText}`);
    }

    return response;
  }

  async handleCartSuccess() {
    await this.refreshCart();
  }

  handleCartError(error) {
    // Could implement user notification here
    console.error('Cart error:', error);
  }

  resetSelections() {
    this.selectedItems.clear();

    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(item => {
      this.setChoiceState(item, false);
      this.clearSelectionForItem(item);
    });

    this.updateCta();
  }

  highlightIncompleteItems() {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);

    for (const itemEl of allItems) {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      if (this.isItemSelected(productId)) continue;

      // Open options if not already open
      const wasOpen = this.isOptionsOpen(itemEl);
      if (!wasOpen) {
        this.openOptions(itemEl);
      }

      // Scroll to the item
      this.scrollToItem(itemEl);

      // Wait for open animation before flashing
      const delay = wasOpen ? 0 : 350;
      setTimeout(() => {
        const fieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');
        let flashedAny = false;

        fieldsets.forEach(fieldset => {
          const hasSelection = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);
          if (!hasSelection) {
            this.flashElement(fieldset);
            flashedAny = true;
          }
        });

        // Fallback: flash the choice row if no fieldsets visible yet
        if (!flashedAny) {
          const choice = itemEl.querySelector(VariantPickerSets.SELECTORS.CHOICE);
          if (choice) this.flashElement(choice);
        }
      }, delay);

      return; // Only handle first incomplete item
    }
  }

  highlightMissingTypeItems(missingBra, missingPanty) {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    let firstHandled = false;

    for (const itemEl of allItems) {
      const isMissingType =
        (missingBra && this.hasRequiredTag(itemEl, this.requiredTags.BRA)) ||
        (missingPanty && this.hasRequiredTag(itemEl, this.requiredTags.PANTY));

      if (!isMissingType) continue;

      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      if (this.isItemSelected(productId)) continue;

      const wasOpen = this.isOptionsOpen(itemEl);
      if (!wasOpen) this.openOptions(itemEl);

      if (!firstHandled) {
        this.scrollToItem(itemEl);
        firstHandled = true;
      }

      const delay = wasOpen ? 0 : 350;
      setTimeout(() => {
        const fieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');
        let flashedAny = false;

        fieldsets.forEach(fieldset => {
          const hasSelection = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);
          if (!hasSelection) {
            this.flashElement(fieldset);
            flashedAny = true;
          }
        });

        if (!flashedAny) {
          const choice = itemEl.querySelector(VariantPickerSets.SELECTORS.CHOICE);
          if (choice) this.flashElement(choice);
        }
      }, delay);
    }
  }

  scrollToItem(itemEl) {
    const header = document.querySelector('.shopify-section--header');
    const offset = (header ? header.offsetHeight : 0) + 40;
    const elementPosition = itemEl.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }

  flashElement(element) {
    const triggerAnimation = (el, cls) => {
      el.classList.remove(cls);
      void el.offsetWidth; // force reflow to restart animation
      el.classList.add(cls);
      el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
    };

    const buttons = element.querySelectorAll(VariantPickerSets.SELECTORS.VALUE_BUTTON);
    if (buttons.length > 0) {
      // Shake the values row
      const valuesRow = element.querySelector('.variant-picker-sets__values');
      if (valuesRow) triggerAnimation(valuesRow, 'sets-shake-error');

      // Flash each available button red
      buttons.forEach(btn => {
        if (!btn.disabled) triggerAnimation(btn, 'sets-flash-error');
      });
    } else {
      // Fallback: shake the element itself
      triggerAnimation(element, 'sets-shake-error');
    }
  }

  scrollToFirstProduct() {
    const firstItem = this.querySelector(VariantPickerSets.SELECTORS.ITEM);
    if (!firstItem) return;

    const offset = document.querySelector('.shopify-section--header').offsetHeight + 40;
    const elementPosition = firstItem.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  showToastMessage(message) {
    const toastElement = document.createRange().createContextualFragment(`
      <div class="banner banner--success" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 10000; max-width: 400px; animation: slideInRight 0.3s ease-out;">
        ${message}
      </div>
    `).firstElementChild;

    document.body.appendChild(toastElement);

    setTimeout(() => {
      if (toastElement && toastElement.parentNode) {
        toastElement.style.animation = 'slideOutRight 0.3s ease-out';
        setTimeout(() => {
          toastElement.remove();
        }, 300);
      }
    }, 2500);
  }

  async refreshCart() {
    const sectionsToBundle = [];

    // Prepare sections
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    // Fetch cart data
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

    // Fetch sections if needed
    let sectionsHtml = {};
    if (sectionsToBundle.length > 0) {
      sectionsHtml = await (await fetch(
        `${Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`
      )).json();
    }

    cartContent.sections = sectionsHtml;

    // Dispatch cart change event
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: null,
          cart: cartContent
        }
      })
    );
  }
}

// Register custom element
if (!window.customElements.get('variant-picker-sets')) {
  window.customElements.define('variant-picker-sets', VariantPickerSets);
}